/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { isEmpty } from "lodash";
import { ASSESMENT_STEPS_ACTIONS } from "../context/actions/ActionTypes";
import { type ActionType } from "../context/reducers/assessmentStepsReducer";
import { type Options } from "./assessments/custom/practices/editModelColumns";
import {
  getIdAfterSegment,
  withWorkspaceId,
} from "shared/utils/workspaceRouting";

export const goToStepPage = ({
  navigate,
  assessmentStepsDispatch,
  url,
  activeStep,
}: {
  navigate: any;
  assessmentStepsDispatch: (value: ActionType) => void;
  url: string;
  activeStep: number;
}): void => {
  assessmentStepsDispatch({
    type: ASSESMENT_STEPS_ACTIONS.UPDATE_CURRENT_STEP,
    payload: activeStep,
  });
  navigate(withWorkspaceId(url));
};

export const getLabelNameFromColumn = (
  fieldId: string,
  // type for id is number | string | undefined | any
  id: any,
  columns: any,
  sectionName: string
): string => {
  if (!id || isEmpty(columns)) return "";
  const columnField = columns[sectionName]?.find(
    (field: any) => field.fieldId === fieldId
  );
  const res = columnField?.options?.find((option: Options) => option.id === id);
  return res?.name ?? "";
};

export const getLabelName = (
  field: string,
  id: number | undefined | string,
  metaData: any
): string => {
  if (!id || isEmpty(metaData)) return "-";
  const res = metaData[field]?.find((option: Options) => option.id === id);
  return res?.name ?? "";
};

export const getDynamicOptionsFromMetaDataBasedOnFertilizerType = (
  metaData: any,
  optionsFieldName: string,
  fertilizerType: string
): any => {
  if (!fertilizerType) {
    return null;
  }
  const fertilizerTypeData: any = metaData.fertilizerTypes.find(
    ({ id }: any) => id === fertilizerType
  );
  const optionList = fertilizerTypeData[optionsFieldName].map((item: any) => ({
    id: item,
    name: item,
  }));
  return optionList;
};
interface GetPathIds {
  organizationID: string;
  assessmentID: string | null;
}
export const getPathIds = (pathname: string): GetPathIds => {
  const organizationID = getIdAfterSegment(pathname, "organization");
  const assessmentID =
    getIdAfterSegment(pathname, "assessment") === "edit"
      ? getIdAfterSegment(pathname, "edit") || null
      : null;
  return { organizationID, assessmentID };
};

// isNumericVal will return true only for numeric value.
export const isNumericVal = (value: string): boolean => /^\d+$/.test(value);

/**
 * Create a numeric validation rule with configurable decimal places and max digits
 * @param t Translation function
 * @param fieldName Human-readable field name for error messages
 * @param maxDecimalPlaces Maximum allowed decimal places (default 4)
 * @param maxDigits Maximum allowed digits (default 12)
 */
export const getNumericValidation = (
  t: any,
  fieldName: string,
  maxDecimalPlaces = 4,
  maxDigits = 12
): any[] => [
  {
    validator: async (_: any, value: number) => {
      if (!value) return;
      const valueStr = value.toString();
      const noDecimalValue = valueStr.replaceAll(".", "");
      if (valueStr.includes(".")) {
        const [integerPart, decimalPart] = valueStr.split(".");
        // maxDigits caps the integer portion regardless of whether the value
        // also carries a decimal part - without this, a value like
        // 12345678901.1 skipped the digit-count check entirely.
        const integerDigits = integerPart.replace("-", "");
        if (decimalPart && decimalPart.length > maxDecimalPlaces) {
          throw new Error(
            t(
              `${fieldName} can have maximum ${maxDecimalPlaces} decimal places.`
            )
          );
        } else if (integerDigits.length > maxDigits) {
          throw new Error(t(`${fieldName} cannot exceed ${maxDigits} digits.`));
        } else if (!isNumericVal(noDecimalValue)) {
          throw new Error(t("Only Numbers are allowed."));
        }
      } else if (valueStr.length > maxDigits) {
        throw new Error(t(`${fieldName} cannot exceed ${maxDigits} digits.`));
      } else if (!isNumericVal(noDecimalValue)) {
        throw new Error(t("Only Numbers are allowed."));
      }
    },
  },
];

/**
 * Create a percentage validation rule (0-max, max 4 decimal places)
 * @param t Translation function
 * @param max Highest accepted value; defaults to 100, but some fields the API
 * validates more tightly pass their own (e.g. bad fruit percent tops out at 99)
 */
export const getPercentageValidation = (t: any, max = 100): any[] => [
  {
    validator: async (_: any, value: number) => {
      if (value === undefined || value === null || String(value) === "") return;
      if (value < 0 || value > max)
        throw new Error(t(`Value must be between 0 and ${String(max)}.`));
      const valueStr = value.toString();
      if (valueStr.includes(".")) {
        const decimalPart = valueStr.split(".")[1];
        if (decimalPart && decimalPart.length > 4)
          throw new Error(t("Value can have maximum 4 decimal places."));
      }
    },
  },
];

/**
 * Cross-field validation (antd rule-render form): the numbers entered across a
 * group of sibling percentage fields — e.g. every disposal method for one plant
 * part — must add up to exactly 100%. The current field being blank is left to
 * a separate `required` rule (this validator only checks the sum once a value
 * is present). Also rejects any non-numeric value in the field being
 * validated. Returned as a `(form) => rule` render function so the validator
 * can read its sibling fields' current values from the form.
 * @param t Translation function
 * @param parentName Form panel the fields live under (e.g. "residue")
 * @param siblingFieldIds Field ids that together make up one section
 * @param sectionLabel Human-readable section name, used in the error message
 */
export const getSectionSumValidation = (
  t: any,
  parentName: string,
  siblingFieldIds: string[],
  sectionLabel: string
): any => {
  return ({ getFieldValue }: { getFieldValue: (name: any) => any }) => ({
    validator: async (_: any, value: any) => {
      if (value === undefined || value === null || value === "") return;
      // Only numbers are allowed in the field.
      if (isNaN(Number(value))) {
        throw new Error(t("Only Numbers are allowed."));
      }
      const total = siblingFieldIds.reduce((sum: number, fieldId: string) => {
        const raw = getFieldValue([parentName, fieldId]);
        const num = Number(raw);
        if (raw === undefined || raw === null || raw === "" || isNaN(num)) {
          return sum;
        }
        return sum + num;
      }, 0);
      if (total !== 100) {
        throw new Error(
          t(
            `${sectionLabel} percentages must total exactly 100% (currently ${total}%).`
          )
        );
      }
    },
  });
};

/**
 * True when the given pathway denotes a Perennial pathway. Centralizes the
 * (case-insensitive substring) rule shared by the basic-info, practices and
 * yield steps so "what counts as perennial" lives in one place.
 */
export const isPerennialPathway = (pathway?: string | null): boolean =>
  typeof pathway === "string" && pathway.toLowerCase().includes("perennial");

/**
 * True when the given pathway denotes a Rice pathway. Same (case-insensitive
 * substring) rule as the Perennial one above, kept here for the same reason.
 * "paddy" counts too: it is the same crop under the name the applications step
 * already uses for it, so a pathway named either way has to behave the same.
 */
export const isRicePathway = (pathway?: string | null): boolean =>
  typeof pathway === "string" &&
  ["rice", "paddy"].some((token) => pathway.toLowerCase().includes(token));

/**
 * True when a crop matches by CFT id or by name — the id is the dependable
 * half, the name a fallback for a crop the static list has no id for.
 * `cftCropId` is compared as text because it arrives as a number from the API
 * but as a string once round-tripped through the basic-info form; comparing
 * with a bare `===` against a number literal (as call sites used to) silently
 * stops matching once that happens.
 */
export const matchesCftCrop = (
  cftCropId: number | string | null | undefined,
  cropName: string | null | undefined,
  targetId: number,
  targetName: string
): boolean =>
  (cftCropId !== null &&
    cftCropId !== undefined &&
    String(cftCropId) === String(targetId)) ||
  cropName === targetName;

/**
 * Turns a name the metadata sends into display text: underscores become spaces,
 * runs of whitespace collapse, and the result reads as a sentence:
 * "STRAW_REMOVED" -> "Straw removed". For names the metadata sends in its own
 * casing. Display only — the value sent back to the API is the option id and is
 * never touched. Applied after translation so a translated label is cased too.
 */
export const toDisplayLabel = (text?: string | null): string => {
  const spaced = String(text ?? "")
    .replaceAll("_", " ")
    .replaceAll(/\s+/g, " ")
    .trim();
  if (!spaced) return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/**
 * Rejects anything that isn't a whole number. The fields it guards strip
 * "." as it is typed, so this catches values that arrive any other way —
 * pasted, or imported from Cropwise — before the API rejects them.
 * @param t Translation function
 */
export const getWholeNumberValidation = (t: any): any[] => [
  {
    validator: async (_: any, value: any) => {
      if (value === undefined || value === null || value === "") return;
      if (!Number.isInteger(Number(value))) {
        throw new TypeError(t("Value must be a whole number."));
      }
    },
  },
];

/**
 * The longest crop life cycle the API accepts, in years. Basic info enforces
 * it on the way in, and it doubles as the cap on how many per-year boxes the
 * Practices/Yield steps render — one box per year of a plausible cycle, and a
 * clear correction message rather than thousands of inputs beyond it.
 */
export const MAX_CROP_LIFE_CYCLE_DURATION = 75;

/**
 * The shortest crop life cycle the API accepts, in years. A perennial that
 * lives a single year isn't a perennial, so the pathway needs at least two.
 */
export const MIN_CROP_LIFE_CYCLE_DURATION = 2;

/**
 * The calendar year an assessment belongs to, taken from its createdAt stamp.
 * Read as UTC, and straight off the ISO string where possible: turning the
 * stamp into a local Date can land on the previous year for anyone west of
 * UTC when the assessment was created in the first hours of January.
 */
export const getAssessmentYear = (
  createdAt?: string | null
): number | undefined => {
  if (typeof createdAt !== "string" || createdAt === "") return undefined;
  const isoYear = /^(\d{4})-/.exec(createdAt);
  if (isoYear) return Number(isoYear[1]);
  const parsed = new Date(createdAt);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.getUTCFullYear();
};

/**
 * First calendar year of the crop life cycle — the year the plant went in the
 * ground. Cool Farm anchors the lifecycle on the assessment year and the crop's
 * age, counting BACK to the planting year and then forward over the cycle:
 *
 *   startYear = assessmentYear - max(inputLifeCycleYear - 1, 0)
 *
 * so an assessment made in 2024 for a crop in its 10th year starts at 2015, one
 * for a crop in its 60th year starts at 1965, and a crop just planted this year
 * (age 0) starts at the assessment year itself rather than a year in the
 * future. `inputLifeCycleYear` is optional on `GenericData` and isn't backfilled
 * on assessments created before this field existed, so callers should expect
 * `undefined` beyond just the first render.
 * @param assessmentYear Year the assessment was created
 * @param inputLifeCycleYear Age of the crop, in years, at the assessment
 */
export const getPerYearStartYear = (
  assessmentYear?: number | null,
  inputLifeCycleYear?: number | null
): number | undefined => {
  const isUsable = (value?: number | null): value is number =>
    typeof value === "number" && Number.isFinite(value);
  if (!isUsable(assessmentYear) || !isUsable(inputLifeCycleYear)) {
    return undefined;
  }
  return assessmentYear - Math.max(Math.floor(inputLifeCycleYear) - 1, 0);
};

/**
 * The calendar years a per-year list covers, oldest first: `count` years
 * running forward from `startYear`. Labelling the boxes with real years rather
 * than "Year 1...Year 5" saves the user working out which season each value
 * belongs to. With no startYear there is no year to show, so this returns [].
 * @param count How many years the list covers
 * @param startYear First year of the cycle (see getPerYearStartYear)
 */
export const getPerYearLabelYears = (
  count: number,
  startYear?: number
): number[] =>
  count > 0 && typeof startYear === "number" && Number.isFinite(startYear)
    ? Array.from({ length: count }, (_, index) => startYear + index)
    : [];

/**
 * Spread a stored per-year number array across the year boxes the edit form
 * renders: exactly `years` entries, padded with blanks when fewer values were
 * stored and truncated when the crop life cycle duration has since shrunk (so
 * a stale extra year can never be saved back). Falls back to whatever was
 * stored when the duration isn't known yet.
 */
export const toPerYearFormValues = (
  value: number[] | null | undefined,
  years?: number
): Array<number | undefined> => {
  const stored = Array.isArray(value) ? value : [];
  // An out-of-range duration renders no boxes, so leave the stored values
  // exactly as they are: padding them would blank the saved data out the
  // next time the panel is saved.
  const count =
    typeof years === "number" &&
    Number.isFinite(years) &&
    years > 0 &&
    years <= MAX_CROP_LIFE_CYCLE_DURATION
      ? Math.floor(years)
      : stored.length;
  return Array.from({ length: count }, (_, index) => stored[index]);
};

/**
 * Read the per-year boxes back into the number array the API stores. Entries
 * left blank are dropped rather than sent as 0 — a half-filled list is caught
 * by the fields' own required rule, not silently zero-filled here.
 */
export const toPerYearNumberArray = (value: any): number[] => {
  const entries = Array.isArray(value) ? value : [];
  return entries
    .filter(
      (entry: any) => entry !== undefined && entry !== null && entry !== ""
    )
    .map(Number)
    .filter((entry: number) => !Number.isNaN(entry));
};
